import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  VideoLibrary as VideoIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { trailsService } from '../services/trailsService';
import { Trail } from '../types';

const ExploreTrails: React.FC = () => {
  const [trails, setTrails] = useState<Trail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchTrails();
  }, []);

  const fetchTrails = async () => {
    try {
      setLoading(true);
      const data = await trailsService.getAll();
      // Filtrar apenas trilhas ativas e que tenham pelo menos 1 devocional
      setTrails(
        data.filter(
          (trail) =>
            trail.isActive &&
            trail.devotionals &&
            trail.devotionals.length > 0
        )
      );
      setError('');
    } catch (err: any) {
      setError('Erro ao carregar trilhas');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleTrailClick = (trailId: string) => {
    navigate(`/explore/${trailId}`);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '80vh',
          background: 'linear-gradient(135deg, #E8F5E9 0%, #F1F8E9 50%, #FFF9C4 100%)',
        }}
      >
        <CircularProgress size={60} sx={{ color: '#4CAF50' }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #E8F5E9 0%, #F1F8E9 50%, #FFF9C4 100%)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `radial-gradient(circle at 20px 20px, rgba(76, 175, 80, 0.05) 2px, transparent 0)`,
          backgroundSize: '40px 40px',
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: 8 }}>
        {/* Hero Section */}
        <Box sx={{ mb: 8, textAlign: 'center' }}>
          {/* Decorative element */}
          <Box
            sx={{
              display: 'inline-block',
              mb: 3,
              px: 3,
              py: 1,
              background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(102, 187, 106, 0.1) 100%)',
              borderRadius: 10,
              border: '2px solid rgba(76, 175, 80, 0.2)',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.9rem',
                fontWeight: 600,
                color: '#2E7D32',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
              }}
            >
              Crescimento Espiritual
            </Typography>
          </Box>

          <Typography
            variant="h1"
            sx={{
              fontWeight: 800,
              background: 'linear-gradient(135deg, #1B5E20 0%, #2E7D32 50%, #4CAF50 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              mb: 3,
              fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem' },
              lineHeight: 1.2,
              textShadow: '0 2px 20px rgba(76, 175, 80, 0.1)',
            }}
          >
            Trilhas de Devocionais
          </Typography>

          <Typography
            variant="h5"
            sx={{
              color: '#558B2F',
              maxWidth: '800px',
              mx: 'auto',
              fontSize: { xs: '1.1rem', md: '1.4rem' },
              lineHeight: 1.6,
              fontWeight: 400,
              mb: 2,
            }}
          >
            Escolha uma trilha e comece sua jornada espiritual diária
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: '#689F38',
              maxWidth: '600px',
              mx: 'auto',
              fontSize: { xs: '0.95rem', md: '1.05rem' },
              opacity: 0.9,
            }}
          >
            Conteúdos inspiradores para fortalecer sua fé e transformar seu dia
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        )}

        {trails.length === 0 && !loading && (
          <Box
            sx={{
              textAlign: 'center',
              py: 10,
              px: 4,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              borderRadius: 4,
              boxShadow: '0 8px 32px rgba(76, 175, 80, 0.15)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(76, 175, 80, 0.1)',
            }}
          >
            <VideoIcon sx={{ fontSize: 100, color: '#81C784', mb: 3, opacity: 0.6 }} />
            <Typography
              variant="h4"
              sx={{
                color: '#2E7D32',
                mb: 2,
                fontWeight: 600,
              }}
            >
              Nenhuma trilha disponível
            </Typography>
            <Typography variant="body1" sx={{ color: '#689F38', fontSize: '1.1rem' }}>
              Em breve teremos novos conteúdos inspiradores para você!
            </Typography>
          </Box>
        )}

        {/* Trails Grid */}
        <Grid container spacing={4}>
          {trails.map((trail) => (
            <Grid item xs={12} sm={6} md={4} key={trail.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  borderRadius: 4,
                  overflow: 'hidden',
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 8px 24px rgba(76, 175, 80, 0.12)',
                  border: '1px solid rgba(76, 175, 80, 0.08)',
                  '&:hover': {
                    transform: 'translateY(-12px) scale(1.02)',
                    boxShadow: '0 20px 40px rgba(76, 175, 80, 0.25)',
                    borderColor: 'rgba(76, 175, 80, 0.3)',
                  },
                }}
                onClick={() => handleTrailClick(trail.id)}
              >
                {/* Thumbnail */}
                <CardMedia
                  component="div"
                  sx={{
                    height: 220,
                    backgroundImage: trail.thumbnailUrl
                      ? `linear-gradient(rgba(76, 175, 80, 0.1), rgba(46, 125, 50, 0.2)), url(${trail.thumbnailUrl})`
                      : 'linear-gradient(135deg, #2E7D32 0%, #4CAF50 50%, #66BB6A 100%)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {!trail.thumbnailUrl && (
                    <VideoIcon sx={{ fontSize: 90, color: 'white', opacity: 0.5 }} />
                  )}

                  {/* Play Overlay */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(0,0,0,0)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'background-color 0.3s',
                      '&:hover': {
                        backgroundColor: 'rgba(0,0,0,0.3)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(255,255,255,0.9)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0,
                        transition: 'opacity 0.3s',
                        '.MuiCard-root:hover &': {
                          opacity: 1,
                        },
                      }}
                    >
                      <PlayIcon sx={{ fontSize: 32, color: '#4CAF50', ml: 0.5 }} />
                    </Box>
                  </Box>
                </CardMedia>

                {/* Content */}
                <CardContent sx={{ flexGrow: 1, p: 3.5, background: 'linear-gradient(to bottom, rgba(255,255,255,0.95), rgba(232,245,233,0.3))' }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      background: 'linear-gradient(135deg, #1B5E20 0%, #2E7D32 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      mb: 2,
                      lineHeight: 1.3,
                      fontSize: '1.4rem',
                    }}
                  >
                    {trail.title}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: '#558B2F',
                      mb: 3,
                      lineHeight: 1.7,
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      fontSize: '0.95rem',
                    }}
                  >
                    {trail.description}
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 'auto' }}>
                    <Chip
                      icon={<VideoIcon sx={{ color: '#2E7D32 !important' }} />}
                      label={`${trail.devotionals?.length || 0} devocionais`}
                      size="small"
                      sx={{
                        background: 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)',
                        color: '#1B5E20',
                        fontWeight: 600,
                        border: '1px solid rgba(76, 175, 80, 0.2)',
                        fontSize: '0.85rem',
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* CTA Section */}
        {trails.length > 0 && (
          <Box
            sx={{
              mt: 10,
              p: { xs: 4, md: 8 },
              textAlign: 'center',
              position: 'relative',
              background: 'linear-gradient(135deg, #1B5E20 0%, #2E7D32 50%, #4CAF50 100%)',
              borderRadius: 4,
              color: 'white',
              overflow: 'hidden',
              boxShadow: '0 12px 40px rgba(76, 175, 80, 0.3)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `radial-gradient(circle at 30px 30px, rgba(255, 255, 255, 0.1) 2px, transparent 0)`,
                backgroundSize: '60px 60px',
              },
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  mb: 2,
                  fontSize: { xs: '2rem', md: '3rem' },
                  textShadow: '0 2px 20px rgba(0,0,0,0.2)',
                }}
              >
                Comece sua jornada hoje!
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  mb: 4,
                  opacity: 0.95,
                  fontSize: { xs: '1rem', md: '1.25rem' },
                  maxWidth: '600px',
                  mx: 'auto',
                  lineHeight: 1.6,
                }}
              >
                Dedique alguns minutos do seu dia para crescer espiritualmente e fortalecer sua fé
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => trails.length > 0 && handleTrailClick(trails[0].id)}
                sx={{
                  backgroundColor: 'white',
                  color: '#2E7D32',
                  px: 5,
                  py: 2,
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  borderRadius: 3,
                  textTransform: 'none',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: '#F1F8E9',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 30px rgba(0,0,0,0.3)',
                  },
                }}
              >
                Explorar Primeira Trilha
              </Button>
            </Box>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default ExploreTrails;
